import {
  MODAL_AUTH_SETUP_ACCOUNT,
  MODAL_AUTH_VERIFY_EMAIL,
} from '@components/App/Modal/constants'
import type { PayloadAction } from '@reduxjs/toolkit'
import { LoaderActions } from '@store/loaders'
import { AuthActions } from '@store/reducers/auth/actions'
import { managerActions } from '@store/reducers/manager/actions'
import type { FetchAuthPayload } from '@store/requestsSaga/types'
import { isAuthUnavailable } from '@store/requestsSaga/utils'
import * as ActionTypes from '@storeOld/actions'
import { openModal } from '@storeOld/reducers/ui'
import axios, { type AxiosResponse } from 'axios'
import { call, put, select } from 'redux-saga/effects'

import API from '@/api'
import { getPlanTier, pushPlanTier } from '@/constants/gtm'
import { authSelector } from '@/hooks/useAuth'
import type { AuthState } from '@/types'

const fetchAuthData = (token: string): Promise<AxiosResponse<any>> =>
  axios.get(`/my`, {
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'X-Auth-Token': token,
    },
  })

export function* fetchAuth(action: PayloadAction<FetchAuthPayload>) {
  const { providedToken } = action.payload ?? {}
  try {
    const auth: AuthState = yield select(authSelector)

    if (!auth.authenticated && !providedToken) {
      yield put(LoaderActions.set('authentication'))
    }

    const token = providedToken || localStorage.getItem('token')

    if (!token) {
      throw new Error('Authentication token not found')
    }

    // @ts-expect-error
    const authResponse = yield call(fetchAuthData, token)

    if (authResponse.status !== 200) {
      throw new Error('Authentication failed')
    }

    const { data } = authResponse

    yield put({ type: ActionTypes.SET_AUTH, payload: data })
    pushPlanTier(getPlanTier(data.planId))
    // @ts-expect-error
    API.Auth.token = token

    // Права приходят вместе с проектом, и он запрашивается с тем же токеном
    // из localStorage, что и /my — на старте перечитывать их незачем.
    // Нужно только когда токен только что сменился (логин, регистрация,
    // oauth): проект был загружен гостем или под другим аккаунтом
    if (providedToken && auth.id !== data.id) {
      yield put(managerActions.refetchProjectAccessSaga())
    }

    if (!data.emailVerified) {
      yield put(
        // @ts-expect-error
        openModal(MODAL_AUTH_VERIFY_EMAIL, {
          disabledClose: true,
          isLocked: true,
          email: data.emailAddress,
          emailVerificationCodeExpiresAt: data.emailVerificationCodeExpiresAt,
        })
      )

      return
    }

    if (!data.setupCompleted) {
      yield put(
        // @ts-expect-error
        openModal(MODAL_AUTH_SETUP_ACCOUNT, {
          disabledClose: true,
          isLocked: true,
          oauthState: {},
        })
      )
    }
  } catch (error) {
    console.error(error)

    // сеть/бэк недоступны — юзер не гость, мы просто не смогли проверить:
    // токен и стейт не трогаем, после перезагрузки fetchAuth повторится
    if (isAuthUnavailable(error)) {
      yield put(AuthActions.unavailable())

      return
    }

    yield put({ type: ActionTypes.RESET_AUTH })
    pushPlanTier(null)
  } finally {
    yield put(LoaderActions.unset('authentication'))
  }
}
